import type { CollegeProgram, CollegeProgramsRelease } from '@backrs_ui/common-ui';

import { useRegisterAgentContext } from '@backrs_ui/grow-ui/src/agentContext/useRegisterAgentContext';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import type { DegreeMix, EarningsStats } from './programStats';

const STRINGS = appStrings.modules.college.whatStudentsStudy;

export const useAgentContext = (
  schoolName: string,
  release: CollegeProgramsRelease | null,
  levelLabel: string | null,
  mix: DegreeMix,
  stats: EarningsStats | null,
  earningsRows: readonly CollegeProgram[],
) => {
  const metadata = release?.metadata ?? null;

  useRegisterAgentContext(
    levelLabel
      ? {
          kind: 'section',
          title: STRINGS.header.title,
          description: `${STRINGS.header.description} Currently showing the ${levelLabel} tab for ${schoolName}.`,
          detail: [
            STRINGS.degreeMix.info.definition,
            ...STRINGS.degreeMix.info.bullets(
              mix.totalAwards.toLocaleString(),
              levelLabel.toLowerCase(),
              mix.programCount,
              metadata?.awards.latest_academic_year ?? null,
            ),
            STRINGS.earnings.info.definition(
              metadata?.earnings.yr4.cohort_academic_years ?? null,
              metadata?.earnings.yr4.dollar_year ?? null,
            ),
            ...STRINGS.earnings.info.bullets,
            STRINGS.earnings.info.source,
          ].join('\n'),
        }
      : null,
  );

  useRegisterAgentContext(
    levelLabel
      ? {
          kind: 'visual',
          label: STRINGS.degreeMix.title,
          display:
            'Donut chart of degrees awarded per year by broad field of study, with the largest field and largest program called out',
          data: {
            level: levelLabel,
            total_awards: mix.totalAwards,
            program_count: mix.programCount,
            categories: mix.categories.map((entry) => ({
              category: entry.category.label,
              awards: entry.awards,
              share: entry.share,
            })),
            largest_field: mix.largestCategory
              ? { name: mix.largestCategory.category.label, share: mix.largestCategory.share }
              : null,
            largest_program: mix.largestProgram
              ? { name: mix.largestProgram.program.cip_desc, share: mix.largestProgram.share }
              : null,
          },
        }
      : null,
  );

  useRegisterAgentContext(
    levelLabel && stats
      ? {
          kind: 'visual',
          label: STRINGS.earnings.title,
          display:
            'Three KPI cards (median of program medians, middle 50% range, full range) and a sortable table of programs with degrees per year and median earnings four years after completion',
          data: {
            level: levelLabel,
            programs_reporting: stats.reportingProgramCount,
            programs_with_current_awards: stats.currentProgramCount,
            median: stats.median,
            iqr: stats.iqr,
            range: stats.range,
            lowest_program: stats.lowestProgram.cip_desc,
            highest_program: stats.highestProgram.cip_desc,
            programs: earningsRows.map((program) => ({
              name: program.cip_desc,
              awards_per_year: program.awards.latest,
              median_earnings_4yr: program.earnings.yr4.median,
            })),
          },
        }
      : null,
  );
};
